/**
 * OnboardingTour — first-login multi-card tour. Pops a single sequential
 * modal sheet of how-to cards over the live UI; user taps "Next" through
 * them or "Skip" once. Resolution is server-side
 * (`users.onboarding_seen_at`) so it stays dismissed across devices.
 *
 * Mounted once in AppShell. Idempotent — fetches `/api/help/onboarding`
 * on user resolve; if `seen` is true or `cards: []` we render nothing.
 */
import React, { useEffect, useState } from 'react';
import { X, ChevronRight, ChevronLeft, Sparkles, Lightbulb, Check } from 'lucide-react';
import { authFetch } from '../utils/authUtils';
import { IconFor, renderInline } from '../utils/helpUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const OnboardingTour = ({ user }) => {
  const [cards, setCards] = useState([]);
  const [idx, setIdx] = useState(0);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Local-storage shortcut so we don't even hit the API for users who
  // already dismissed on this device. Server-side flag remains the
  // source of truth on a fresh device.
  const lsKey = user?.user_id ? `sy_onboarding_seen_${user.user_id}` : null;

  useEffect(() => {
    if (!user?.user_id) return;
    if (lsKey && localStorage.getItem(lsKey) === '1') return;
    let cancelled = false;
    (async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/api/help/onboarding`);
        if (!r.ok) return;
        const d = await r.json();
        if (cancelled) return;
        if (d.seen) {
          if (lsKey) localStorage.setItem(lsKey, '1');
          return;
        }
        if (!d.cards || d.cards.length === 0) {
          // Nothing to show for this role → fast-finish so users in this
          // role don't get re-queried every page load.
          markSeen(/*silent=*/true);
          return;
        }
        setCards(d.cards);
        setIdx(0);
        setOpen(true);
      } catch { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);

  const markSeen = async (silent = false) => {
    if (submitting) return;
    setSubmitting(true);
    if (lsKey) localStorage.setItem(lsKey, '1');
    try {
      await authFetch(`${BACKEND_URL}/api/help/onboarding/seen`, { method: 'POST' });
    } catch { /* localStorage already set — fine */ }
    if (!silent) setOpen(false);
    setSubmitting(false);
  };

  if (!open || cards.length === 0) return null;

  const card = cards[idx];
  const isLast = idx === cards.length - 1;

  return (
    <div
      className="fixed inset-0 z-[95] flex items-center justify-center p-4"
      data-testid="onboarding-tour"
    >
      <div className="absolute inset-0 bg-black/85 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-md bg-[#0c0a1d] border border-indigo-500/40 rounded-2xl shadow-[0_0_0_1px_rgba(99,102,241,0.2),0_24px_48px_-12px_rgba(99,102,241,0.4)] overflow-hidden"
        style={{ animation: 'tourSlideIn 280ms cubic-bezier(.2,.8,.2,1)' }}
      >
        <style>{`@keyframes tourSlideIn { from { opacity: 0; transform: translateY(16px) scale(.98); } to { opacity: 1; transform: translateY(0) scale(1); } }`}</style>

        {/* Header */}
        <div className="flex items-center justify-between px-4 pt-4 pb-2">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-300" />
            <span className="text-[10px] uppercase tracking-[0.25em] font-black text-amber-300">
              Welcome — quick tour
            </span>
          </div>
          <button
            type="button"
            onClick={() => markSeen(false)}
            className="w-8 h-8 rounded-full text-slate-400 hover:text-white hover:bg-white/5 flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300"
            data-testid="onboarding-skip"
            aria-label="Skip tour"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 pb-2 pt-1">
          <div className="flex items-start gap-3 mb-3">
            <div className="shrink-0 w-12 h-12 rounded-xl bg-indigo-500/20 border border-indigo-500/40 flex items-center justify-center">
              <IconFor name={card.icon} className="w-6 h-6 text-indigo-200" />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-black text-white leading-tight">{card.title}</h2>
              <p className="text-xs text-slate-400 mt-0.5">{card.summary}</p>
            </div>
          </div>

          <ol className="space-y-2 mb-3" data-testid="onboarding-steps">
            {(card.steps || []).map((step, i) => (
              <li key={i} className="flex gap-2.5">
                <span className="shrink-0 w-5 h-5 rounded-full bg-indigo-500/30 text-indigo-100 text-[10px] font-black tabular-nums flex items-center justify-center mt-0.5">
                  {i + 1}
                </span>
                <span className="text-sm text-slate-200 leading-relaxed">{renderInline(step)}</span>
              </li>
            ))}
          </ol>

          {card.pro_tip && (
            <div className="flex items-start gap-2 rounded-lg bg-amber-500/10 border border-amber-500/30 p-2.5 mb-3">
              <Lightbulb className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
              <p className="text-[11px] text-amber-100 leading-relaxed">
                <strong className="font-bold uppercase tracking-widest text-[9px] text-amber-300 mr-1.5">Pro tip</strong>
                {renderInline(card.pro_tip)}
              </p>
            </div>
          )}
        </div>

        {/* Footer — dots + nav */}
        <div className="px-4 py-3 border-t border-slate-800 flex items-center justify-between bg-black/30">
          <div className="flex items-center gap-1.5">
            {cards.map((_, i) => (
              <span
                key={i}
                className={`block h-1.5 rounded-full transition-all ${
                  i === idx ? 'w-5 bg-indigo-300' : 'w-1.5 bg-slate-700'
                }`}
              />
            ))}
            <span className="text-[10px] uppercase tracking-widest font-black text-slate-500 ml-2 tabular-nums">
              {idx + 1}/{cards.length}
            </span>
          </div>

          <div className="flex items-center gap-2">
            {idx > 0 && (
              <button
                type="button"
                onClick={() => setIdx(idx - 1)}
                className="inline-flex items-center gap-1 text-xs font-bold text-slate-300 hover:text-white px-2.5 py-1.5 rounded-md hover:bg-white/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300"
                data-testid="onboarding-prev"
              >
                <ChevronLeft className="w-3.5 h-3.5" /> Back
              </button>
            )}
            {!isLast ? (
              <button
                type="button"
                onClick={() => setIdx(idx + 1)}
                className="inline-flex items-center gap-1.5 text-xs font-black bg-indigo-600 hover:bg-indigo-500 text-white px-3 py-2 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300"
                data-testid="onboarding-next"
              >
                Next <ChevronRight className="w-3.5 h-3.5" />
              </button>
            ) : (
              <button
                type="button"
                onClick={() => markSeen(false)}
                disabled={submitting}
                className="inline-flex items-center gap-1.5 text-xs font-black bg-emerald-500 hover:bg-emerald-400 text-emerald-950 px-3 py-2 rounded-md disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300"
                data-testid="onboarding-finish"
              >
                <Check className="w-3.5 h-3.5" /> Got it
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTour;
